import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';


import {
  Input,
  InputGroup,
  Table,
  Button,
  DOMHelper,
  Stack,
  useToaster,
  Pagination,
  Modal,
  Message,
  IconButton,
  Whisper,
  Tooltip
} from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import PlusIcon from '@rsuite/icons/Plus';
import { MdFilterList, MdDelete } from 'react-icons/md';
import { Edit, Icon } from '@rsuite/icons';
import { useDispatch, useSelector } from 'react-redux';
import LeaveTypeFilterDrawer from './LeaveTypeFilterDrawer';
import AddLeaveTypeDrawer from './AddLeaveTypeDrawer';
import EditLeaveTypeDrawer from './EditLeaveTypeDrawer';
import { fetchFeaturesLeave } from '@/services/feature.service';
import { deleteSetting } from '@/services/feature.service';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const LeaveTypeDataTable = () => {
  const dispatch = useDispatch();
  const toaster = useToaster();

  const { leaveTypes, loading } = useSelector(state => state.feature);

  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [showAddLeaveTypeDrawer, setShowAddLeaveTypeDrawer] = useState(false);
  const [showEditLeaveTypeDrawer, setShowEditLeaveTypeDrawer] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filters, setFilters] = useState({
    type: '',
    category: '',
    increment: '',
    typeBalance: ''
  });
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchFeaturesLeave());
    };
    fetchData();
  }, [dispatch]);

  const handleChangeLimit = dataKey => {
    setPage(1);
    setLimit(dataKey);
  };

  const filteredData = leaveTypes.filter(item => {
    // Filter by type (now a text input)
    if (filters.type && !item.type?.toLowerCase().includes(filters.type.toLowerCase())) {
      return false;
    }

    // Filter by category
    if (filters.category && item.category?.toLowerCase() !== filters.category.toLowerCase()) {
      return false;
    }

    // Filter by increment
    if (filters.increment !== '' && item.Increment !== (filters.increment === 'true')) {
      return false;
    }

    // Filter by type balance
    if (filters.typeBalance && item.typeBalance?.toLowerCase() !== filters.typeBalance.toLowerCase()) {
      return false;
    }

    // Search by type or category
    if (searchKeyword) {
      const searchLower = searchKeyword.toLowerCase();
      const type = item.type?.toLowerCase() || '';
      const category = item.category?.toLowerCase() || '';

      return type.includes(searchLower) || category.includes(searchLower);
    }

    return true;
  });

  const handleFiltersChange = newFilters => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      type: '',
      category: '',
      increment: '',
      typeBalance: ''
    });
    setPage(1);
  };

  const paginatedData = filteredData.filter((v, i) => {
    const start = limit * (page - 1);
    const end = start + limit;
    return i >= start && i < end;
  });

  const handleSearch = value => {
    setSearchKeyword(value);
    setPage(1);
  };

  const handleDelete = rowData => {
    setSelectedLeaveType(rowData);
    setShowDeleteModal(true);
  };

  const handleEdit = rowData => {
    setSelectedLeaveType(rowData);
    setShowEditLeaveTypeDrawer(true);
  };
  const { t } = useTranslation();






  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteSetting(selectedLeaveType._id));

      toaster.push(
        <Message type="success" duration={3000}>
          {t('Leave Type deleted successfully!')}
        </Message>,
        { placement: 'topCenter' }
      );
      setShowDeleteModal(false);

      dispatch(fetchFeaturesLeave());
    } catch (error) {
      toaster.push(
        <Message type="error" duration={3000}>
          {t('Error deleting leave type')}
        </Message>,
        { placement: 'topCenter' }
      );
    }
    setShowDeleteModal(false);
  };

  return (
    <>
      <Stack className="table-toolbar" justifyContent="space-between">
        <InputGroup inside>
          <Input placeholder={t("Search")} value={searchKeyword} onChange={handleSearch} />
          <InputGroup.Addon>
            <SearchIcon />
          </InputGroup.Addon>
        </InputGroup>

        <Stack spacing={6}>
          <Button appearance="primary" onClick={() => setShowFilterDrawer(true)}>
            <MdFilterList style={{ marginRight: 8 }} /> {t('Filters')}
          </Button>
          <Button appearance="primary" onClick={() => setShowAddLeaveTypeDrawer(true)}>
            <PlusIcon style={{ marginRight: 8 }} /> {t('Add Leave Type')}
          </Button>
        </Stack>
      </Stack>
      <>
        <Table
          height={Math.max(getHeight(window) - 200, 400)}
          data={paginatedData}
          loading={loading}
          rowHeight={60}
          wordWrap="break-word"
        >
          <Column flexGrow={3}>
            <HeaderCell>{t('Leave Type')}</HeaderCell>
            <Cell>{rowData => rowData.type || ''}</Cell>
          </Column>

          <Column flexGrow={2}>
            <HeaderCell>{t('Category')}</HeaderCell>
            <Cell>{rowData => rowData.category?.toUpperCase() || ''}</Cell>
          </Column>

          <Column flexGrow={2}>
            <HeaderCell>{t('Number of Days/Months')}</HeaderCell>
            <Cell>{rowData => {
              const days = rowData.NBDays || 0;
              const dayText = days === 0 || days === 1 ? t('Day') : t('Days');
              const daysPart = `${days} ${dayText}`;
              
              const months = rowData.NBMonths || 0;
              if (months > 0 && days === 0) {
                const monthText = months === 1 || months === 0 ? t('Month') : t('Months');
                return `${months} ${monthText}`;
              } else if (months === 0) {
                return daysPart;
              } else {
                return `${months} ${months === 1 ? 'Month' : 'Months'} / ${daysPart}`;
              }
            }}</Cell>
          </Column>

          <Column flexGrow={2}>
            <HeaderCell>{t('Balance Type')}</HeaderCell>
            <Cell>{rowData => {
              if (rowData.typeBalance === 'LeaveBalance') {
                return t('Leave Balance');
              } else if (rowData.typeBalance === 'sickBalance') {
                return t('Sick Balance');
              }
              return '';
            }}</Cell>
          </Column>

          <Column flexGrow={1}>
            <HeaderCell>{t('Increment')}</HeaderCell>
            <Cell>{rowData => (rowData.Increment ? t('Yes') : t('No'))}</Cell>
          </Column>

          {/* <Column flexGrow={1}>
            <HeaderCell>Attachment</HeaderCell>
            <Cell>{rowData => (rowData.Attachement ? 'Yes' : 'No')}</Cell>
          </Column> */}

          <Column width={100} flexGrow={0.5} fixed="right">
            <HeaderCell>{t('Actions')}</HeaderCell>
            <Cell style={{ padding: '6px' }}>
              {rowData => (
                <Stack spacing={6}>
                  <Whisper placement="top" trigger="hover" speaker={<Tooltip>{t('Edit')}</Tooltip>}>
                    <IconButton
                      size="xs"
                      icon={<Icon as={Edit} />}
                      onClick={() => handleEdit(rowData)}
                      id={`edit-${rowData._id}`}
                    />
                  </Whisper>
                  <Whisper placement="top" trigger="hover" speaker={<Tooltip>{t('Delete')}</Tooltip>}>
                    <IconButton
                      size="xs"
                      icon={<Icon as={MdDelete} style={{ color: 'red' }} />}
                      onClick={() => handleDelete(rowData)}
                      id={`delete-${rowData._id}`}
                    />
                  </Whisper>
                </Stack>
              )}
            </Cell>
          </Column>
        </Table>
        <div style={{ padding: 20 }}>
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={filteredData.length}
            limitOptions={[10, 30, 50]}
            limit={limit}
            activePage={page}
            onChangePage={setPage}
            onChangeLimit={handleChangeLimit}
          />
        </div>
      </>

      <LeaveTypeFilterDrawer
        open={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        onFiltersChange={handleFiltersChange}
        onResetFilters={handleResetFilters}
        currentFilters={filters}
        toaster={toaster}
      />

      <AddLeaveTypeDrawer
        open={showAddLeaveTypeDrawer}
        onClose={() => setShowAddLeaveTypeDrawer(false)}
        toaster={toaster}
      />

      <EditLeaveTypeDrawer
        open={showEditLeaveTypeDrawer}
        onClose={() => setShowEditLeaveTypeDrawer(false)}
        toaster={toaster}
        leaveTypeData={selectedLeaveType}
      />

      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>{t('Delete Leave Type')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('Are you sure you want to delete the leave type')} "{selectedLeaveType?.type}"? {t('This action cannot be undone.')}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowDeleteModal(false)} appearance="subtle">
            {t('Cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} appearance="primary" color="red">
            {t('Delete')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default LeaveTypeDataTable;